import { API_BASE } from '../config.js';
import type { CircuitPiece3dCatalogEntry } from '@multirx/shared/game/circuitPieceCatalog.js';

/**
 * Le catalogue ne change jamais en cours de session — pourtant chaque scène 3D
 * (vignette de liste, détail, éditeur) le refetch indépendamment pour résoudre
 * ses modèles (`circuit3dTrackScene.ts`), et il y a désormais une scène 3D
 * quasiment partout. Sans cache, une simple page de liste pouvait déclencher
 * une dizaine de requêtes identiques d'un coup, saturant les connexions du
 * navigateur au moment précis où le sélecteur de pièces/décor a besoin de
 * bande passante pour ses propres aperçus. Mise en cache par catégorie
 * (la clé `'all'` correspond à l'appel sans filtre).
 */
const catalogCache = new Map<string, Promise<CircuitPiece3dCatalogEntry[]>>();

/** Catalogue des pièces 3D circuit disponibles (Kenney Racing Kit). */
export function fetchCircuitPieceCatalog(
  category?: 'road' | 'decor'
): Promise<CircuitPiece3dCatalogEntry[]> {
  const cacheKey = category ?? 'all';
  const cached = catalogCache.get(cacheKey);
  if (cached) return cached;

  const url =
    category != null
      ? `${API_BASE}/api/circuit-pieces-3d?category=${category}`
      : `${API_BASE}/api/circuit-pieces-3d`;
  const promise = fetch(url)
    .then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(
          (err as { error?: string }).error ?? 'Erreur lors du chargement des pièces 3D circuit'
        );
      }
      return res.json() as Promise<CircuitPiece3dCatalogEntry[]>;
    })
    .catch((err) => {
      // Un échec ne doit pas rester en cache indéfiniment — le prochain appel
      // relance un vrai fetch au lieu de rejeter pour toujours.
      catalogCache.delete(cacheKey);
      throw err;
    });
  catalogCache.set(cacheKey, promise);
  return promise;
}
